import React, { useState, useEffect } from 'react';
import { Cookie } from 'lucide-react';
import { CONSENT_KEY, analyticsConfigured, setConsent } from '../../services/attribution';

/**
 * Cookies.
 *
 * Nödvändig lagring (inloggning, varukorg, tema) kräver inget samtycke men
 * besökaren ska informeras. Google Analytics får bara laddas efter ett
 * aktivt "Godkänn statistik" (lagen om elektronisk kommunikation). Finns
 * inget mätnings-id visas bara informationen, som tidigare.
 *
 * Valet kan ändras när som helst via "Cookieinställningar" i sidfoten
 * (openCookieSettings nedan).
 */

const OPEN_EVENT = 'cn:cookie-settings';

/** Öppnar rutan igen, så att besökaren kan ändra sitt val. */
export const openCookieSettings = () => window.dispatchEvent(new Event(OPEN_EVENT));

export const CookieConsent: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const withStats = analyticsConfigured();

    useEffect(() => {
        let stored: string | null = null;
        try {
            stored = localStorage.getItem(CONSENT_KEY);
        } catch {
            // Lagring blockerad — visa inte rutan varje sidvisning.
            stored = 'necessary';
        }
        // Tidigare fanns bara "Okej" (sparat som 'true'). När statistik finns
        // har besökaren inte tagit ställning till den, så rutan visas en gång till.
        const decided = stored === 'necessary' || stored === 'statistics' || (stored === 'true' && !withStats);
        const open = () => setIsVisible(true);
        window.addEventListener(OPEN_EVENT, open);
        if (decided) return () => window.removeEventListener(OPEN_EVENT, open);
        const timer = setTimeout(open, 1000);
        return () => { clearTimeout(timer); window.removeEventListener(OPEN_EVENT, open); };
    }, [withStats]);

    const choose = (consent: 'necessary' | 'statistics') => {
        const hadStats = (() => { try { return localStorage.getItem(CONSENT_KEY) === 'statistics'; } catch { return false; } })();
        setConsent(consent);
        setIsVisible(false);
        // Återkallat samtycke: ladda om så att Google Analytics inte längre körs.
        if (hadStats && consent === 'necessary') window.location.reload();
    };

    if (!isVisible) return null;

    return (
        <div
            role="region"
            aria-label="Cookies"
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-[150] animate-fade-in-up"
        >
            <div className="bg-ink-900/95 backdrop-blur-md text-white p-5 rounded-2xl shadow-2xl border border-white/10 flex flex-col gap-4">
                <div className="flex items-start gap-3">
                    <div className="bg-ink-900 p-2 rounded-full" aria-hidden="true">
                        <Cookie className="text-tone-caramel" size={20} />
                    </div>
                    <div>
                        {withStats ? (
                            <>
                                <h4 className="text-body font-bold mb-1">Cookies</h4>
                                <p className="text-meta text-ink-400 leading-relaxed">
                                    Vi använder det som behövs för att sajten ska fungera, som inloggning och din varukorg.
                                    Om du godkänner använder vi även Google Analytics för att se hur butiken används. Ingen reklam.
                                </p>
                            </>
                        ) : (
                            <>
                                <h4 className="text-body font-bold mb-1">Bara nödvändiga cookies</h4>
                                <p className="text-meta text-ink-400 leading-relaxed">
                                    Vi använder endast det som behövs för att sajten ska fungera, som inloggning och din varukorg.
                                    Ingen spårning eller reklam.
                                </p>
                            </>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                    <a
                        href="#/info/privacy"
                        className="px-3 py-2 text-meta font-bold text-ink-500 hover:text-white transition-colors"
                    >
                        Läs mer
                    </a>
                    {withStats ? (
                        <>
                            <button
                                type="button"
                                onClick={() => choose('necessary')}
                                className="px-4 py-2 border border-white/25 text-white rounded-xl text-meta font-bold hover:bg-white/10 transition-colors"
                            >
                                Bara nödvändiga
                            </button>
                            <button
                                type="button"
                                onClick={() => choose('statistics')}
                                className="px-4 py-2 bg-white text-ink-900 rounded-xl text-meta font-black hover:bg-paper-200 transition-colors shadow-lg"
                            >
                                Godkänn statistik
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            onClick={() => choose('necessary')}
                            className="px-6 py-2 bg-white text-ink-900 rounded-xl text-meta font-black hover:bg-paper-200 transition-colors shadow-lg"
                        >
                            Okej
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};
